'use client';
import { useLayoutEffect, useState } from 'react';
import Image from 'next/image';
import { SvgPause } from '@/components/Icons/SvgPause';
import { SvgPlay } from '@/components/Icons/SvgPlay';
import dynamic from 'next/dynamic';
// Avoid SSR for ReactPlayer to avoid hydration issues
// https://github.com/cookpete/react-player/issues/1428
const ReactPlayer = dynamic(() => import('react-player/lazy'), { ssr: false });

export default function VideoBanner({ block, position }) {
  const [videoPlaying, setVideoPlaying] = useState(true);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [remountKey, setRemountKey] = useState(0);

  // Bump the key to remount on Activity show, since Vimeo keeps
  // trying to pause itself otherwise. Also autoplay again.
  useLayoutEffect(() => {
    return () => {
      setVideoPlaying(true);
      setVideoLoaded(false);
      setRemountKey((key) => key + 1);
    };
  }, []);

  const {
    title,
    multi_line_title,
    video_link,
    poster,
    overlay,
    overlay_color,
    _metadata
  } = block.video_banner;

  //play or pause video on icon click
  function handleClick() {
    setVideoPlaying(!videoPlaying);
  }

  return (
    <section
      id={`${_metadata.uid}_${position}`}
      data-name={'video-banner'}
      className="relative max-h-[600px] overflow-hidden"
    >
      <div className="relative w-full">
        <Image
          src={poster.url}
          width={poster.dimension.width}
          height={poster.dimension.height}
          sizes="100vw"
          alt={poster.title.replace(/\.[^/.]+$/, '')}
          className={`${videoLoaded ? 'invisible' : ''} h-full w-full`}
        />
        <ReactPlayer
          key={remountKey}
          url={video_link}
          loop={true}
          playing={videoPlaying}
          playsinline={true}
          volume={0}
          muted={true}
          width="100%"
          height="100%"
          onPlay={() => setVideoLoaded(true)}
          className="absolute top-0 left-0"
        />
      </div>
      {videoPlaying ? (
        <SvgPause
          className="video-pause-button right-[14px] bottom-4 z-40 h-[40px] w-[40px] sm:right-[21px] sm:bottom-[18px]"
          handleClick={handleClick}
        />
      ) : (
        <SvgPlay
          className="video-play-button right-[14px] bottom-4 z-40 h-[40px] w-[40px] sm:right-[21px] sm:bottom-[18px]"
          handleClick={handleClick}
        />
      )}
      {overlay && (
        <div
          className={`h-full w-full bg-[${overlay_color}] absolute top-0 z-30 opacity-30`}
        ></div>
      )}
      <div
        className={`absolute bottom-[40%] z-30 mx-auto w-full px-5 text-center text-white sm:bottom-[45%] sm:px-10`}
      >
        {title && (
          <h1
            className={`h3 lg:h2 mx-auto max-w-[600px] pb-5 lg:max-w-[640px]`}
          >
            {title}
          </h1>
        )}
        {multi_line_title && (
          <div
            className={`body-r lg:body-l mx-auto max-w-[600px] lg:max-w-[640px]`}
          >
            {multi_line_title}
          </div>
        )}
      </div>
    </section>
  );
}
